import { Injectable } from '@angular/core'
import { HttpClient, HttpHeaders } from '@angular/common/http'
import { of } from 'rxjs'
import { catchError, map } from 'rxjs/operators'
import { environment } from 'src/environments/environment'

/** Categories and subcategories — mirrors stacks-agents `src/app/api/categories/`. */
@Injectable({ providedIn: 'root' })
export class CategoriesApiService {
  constructor(private http: HttpClient) {}

  /** Builder JWT for the stacks-agents API */
  private agentsAuthHeaders(): HttpHeaders {
    return new HttpHeaders({ Authorization: `Bearer ${localStorage.getItem('token')}` })
  }

  getCategories(projectId: string): Promise<any[]> {
    return this.http
      .get<{ data?: any[] }>(`${environment.agentsUrl}v4/retrieval/categories`, {
        headers: this.agentsAuthHeaders(),
        params: { projectId },
      })
      .pipe(
        map(body => body?.data ?? []),
        catchError(() => of([])),
      )
      .toPromise() as Promise<any[]>
  }

  /** Creates a top-level category. @returns The new category UUID, or null on failure. */
  async insertCategory(projectId: string, categoryName: string, categoryImage: string | null, sortOrder: number): Promise<string | null> {
    const res = await this.http
      .post<{ data: { id: string } }>(`${environment.agentsUrl}v4/creation/categories`,
        { projectId, categoryName, categoryImage, sortOrder },
        { headers: this.agentsAuthHeaders() })
      .pipe(catchError(() => of(null)))
      .toPromise()
    return res?.data?.id ?? null
  }

  /** Creates a subcategory under a parent category. @returns The new UUID, or null on failure. */
  async insertSubCategory(projectId: string, parentId: string, subCategoryName: string, subCategoryImage: string | null, sortOrder: number): Promise<string | null> {
    const res = await this.http
      .post<{ data: { id: string } }>(`${environment.agentsUrl}v4/creation/subcategories`,
        { projectId, parentId, subCategoryName, subCategoryImage, sortOrder },
        { headers: this.agentsAuthHeaders() })
      .pipe(catchError(() => of(null)))
      .toPromise()
    return res?.data?.id ?? null
  }

  /** Updates the name, image, and sort order of an existing category. */
  async updateCategory(projectId: string, categoryId: string, categoryName: string, categoryImage: string | null, sortOrder: number): Promise<void> {
    await this.http
      .patch(`${environment.agentsUrl}v4/update/categories`,
        { projectId, categoryId, categoryName, categoryImage, sortOrder },
        { headers: this.agentsAuthHeaders() })
      .pipe(catchError(() => of(null)))
      .toPromise()
  }

  /** Updates the name, image, and sort order of an existing subcategory. */
  async updateSubCategory(projectId: string, subCategoryId: string, subCategoryName: string, subCategoryImage: string | null, sortOrder: number): Promise<void> {
    await this.http
      .patch(`${environment.agentsUrl}v4/update/subcategories`,
        { projectId, subCategoryId, subCategoryName, subCategoryImage, sortOrder },
        { headers: this.agentsAuthHeaders() })
      .pipe(catchError(() => of(null)))
      .toPromise()
  }

  /** Soft-deletes a category or subcategory. */
  async deleteCategory(projectId: string, categoryId: string): Promise<void> {
    await this.http
      .request('DELETE', `${environment.agentsUrl}v4/deletion/categories`, {
        headers: this.agentsAuthHeaders(),
        body: { projectId, categoryId },
      })
      .pipe(catchError(() => of(null)))
      .toPromise()
  }
}
